// What the map draws, from what the server said. Statuses and reasons are the
// server's; this file only decides where boxes sit, which lines join them and how
// many dots a line should carry for the traffic it has (presentation).

import type { OpsOverview, OpsStatus } from '../../api/ops-types.js';
import { cubicPath, link, straight, type Cubic } from './geometry.js';
import { dur, n0, pct, perMinute } from './format.js';

export type NodeId =
  'callers' | 'edge' | 'api' | 'database' | 'worker' | 'relay' | 'jobs' | 'providers';

export const NODE_W = 150;
export const NODE_H = 64;
export const VIEW_W = 1000;
export const VIEW_H = 290;

export interface MapNode {
  readonly id: NodeId;
  readonly name: string;
  readonly sub: string;
  readonly metric: string;
  readonly status: OpsStatus;
  /** A box nothing reports on: drawn dashed, and not something to open. */
  readonly measured: boolean;
  readonly x: number;
  readonly y: number;
}

export type EdgeState = 'ok' | 'warn' | 'bad' | 'idle';

export interface MapEdge {
  readonly id: string;
  readonly from: NodeId;
  readonly to: NodeId;
  readonly curve: Cubic;
  readonly path: string;
  /** Calls per minute on the line (relative volume; the unit differs by line). */
  readonly rate: number;
  readonly dps: number;
  readonly state: EdgeState;
  /** The far end is failing: dots fade before they arrive. */
  readonly drop: boolean;
}

/** The Worker container that holds the relay and the jobs. */
export const WORKER_BOX = { x: 630, y: 8, w: 190, h: 262 } as const;

const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));

/** More traffic, more dots, but a tenfold rise is not tenfold the dots. */
export function dotsPerSecond(rate: number): number {
  return clamp(Math.log10(1 + rate) * 0.7, 0, 3.2);
}

const MAX_NAMES = 24;

/** The providers that were called, as short words that fit a box. */
function providerNames(ov: OpsOverview): string {
  const names = [...new Set(ov.providers.items.map((i) => i.provider))];
  const text = names.join(', ');
  return text.length > MAX_NAMES ? `${names.length} providers` : text;
}

export function buildMap(ov: OpsOverview): { nodes: MapNode[]; edges: MapEdge[] } {
  const { api, worker } = ov.services;
  // Callers are never measured. The edge and the providers are measured once
  // something has reported on them: an outside check, a call to a provider.
  const unmeasured = new Set<NodeId>(['callers']);
  // The edge's only line runs from the callers, which are never measured, so an
  // unmeasured edge needs no entry of its own here.
  if (!ov.providers.measured) unmeasured.add('providers');
  const answered = ov.edge.targets.flatMap((t) =>
    t.availability === null ? [] : [t.availability],
  );
  const availability =
    answered.length === 0 ? null : answered.reduce((a, b) => a + b, 0) / answered.length;
  const providerCalls = ov.providers.items.reduce((n, i) => n + i.calls, 0);
  const providerFailed = ov.providers.items.reduce((n, i) => n + i.rejected + i.unknown, 0);
  const up = (xs: readonly { condition: string }[]): number =>
    xs.filter((m) => m.condition === 'up').length;
  const jobFailures = ov.jobs.items.filter((j) => j.status === 'warn' || j.status === 'bad').length;
  const jobRunsPerMinute = ov.jobs.items.reduce((s, j) => s + j.runs, 0) / (24 * 60);
  const relayPerMinute = ov.relay.claimed / ov.windowMinutes;

  const nodes: MapNode[] = [
    {
      id: 'callers',
      name: 'Callers',
      sub: 'tills, phones, dashboard',
      metric: 'not measured',
      status: 'unknown',
      measured: false,
      x: 10,
      y: 40,
    },
    {
      id: 'edge',
      name: 'Edge and menu pages',
      sub: ov.edge.measured
        ? `${ov.edge.targets.length} outside ${ov.edge.targets.length === 1 ? 'check' : 'checks'}`
        : 'Cloudflare, no outside check',
      metric:
        ov.edge.measured && availability !== null
          ? `${pct(availability)} answered`
          : 'not measured',
      status: ov.edge.status,
      measured: ov.edge.measured,
      x: 10,
      y: 200,
    },
    {
      id: 'api',
      name: 'API',
      sub: `${up(api.machines)} of ${api.machines.length} machines running`,
      metric: `${perMinute(api.traffic.callsPerMinute)} calls/min`,
      status: api.status,
      measured: true,
      x: 215,
      y: 40,
    },
    {
      id: 'database',
      name: 'Postgres',
      sub: `${ov.database.pool.total} connections, ${ov.database.pool.waiting} waiting`,
      metric: ov.database.ok ? `answers in ${ov.database.pingMs} ms` : 'does not answer',
      status: ov.database.status,
      measured: true,
      x: 430,
      y: 40,
    },
    {
      id: 'worker',
      name: 'Worker',
      sub: `${up(worker.machines)} of ${worker.machines.length} machines running`,
      metric: '',
      status: worker.status,
      measured: true,
      x: WORKER_BOX.x + 10,
      y: WORKER_BOX.y + 6,
    },
    {
      id: 'relay',
      name: 'Outbox relay',
      sub: ov.relay.sampled ? `oldest waiting ${dur(ov.relay.oldestMs)}` : 'not sampled lately',
      metric: ov.relay.sampled ? `${n0(ov.relay.depth)} waiting` : 'no sample',
      status: ov.relay.status,
      measured: true,
      x: 650,
      y: 54,
    },
    {
      id: 'jobs',
      name: 'Scheduled jobs',
      sub: jobFailures > 0 ? `${jobFailures} need a look` : 'all recent runs fine',
      metric: `${ov.jobs.items.length} ${ov.jobs.items.length === 1 ? 'job' : 'jobs'} seen`,
      status: ov.jobs.status,
      measured: true,
      x: 650,
      y: 158,
    },
    {
      id: 'providers',
      name: 'Providers',
      sub: ov.providers.measured ? providerNames(ov) : 'SMS, WhatsApp, UPI',
      metric: ov.providers.measured
        ? `${n0(providerCalls)} calls, ${n0(providerFailed)} failed`
        : 'not measured',
      status: ov.providers.status,
      measured: ov.providers.measured,
      x: 850,
      y: 40,
    },
  ];
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // `inbound`: the callers are not measured, but the API counts what arrives from
  // them, so that line carries real traffic even though one end is a dashed box.
  const spec: {
    id: string;
    from: NodeId;
    to: NodeId;
    curve: Cubic;
    rate: number;
    inbound?: boolean;
  }[] = [
    {
      id: 'callers-api',
      from: 'callers',
      to: 'api',
      curve: straight({ x: 160, y: 72 }, { x: 215, y: 72 }),
      rate: api.traffic.callsPerMinute,
      inbound: true,
    },
    {
      id: 'api-database',
      from: 'api',
      to: 'database',
      curve: straight({ x: 365, y: 72 }, { x: 430, y: 72 }),
      rate: api.traffic.callsPerMinute,
    },
    {
      id: 'relay-database',
      from: 'relay',
      to: 'database',
      curve: link({ x: 650, y: 84 }, { x: 580, y: 84 }),
      rate: relayPerMinute,
    },
    {
      id: 'jobs-database',
      from: 'jobs',
      to: 'database',
      curve: link({ x: 650, y: 180 }, { x: 580, y: 96 }),
      rate: jobRunsPerMinute,
    },
    {
      id: 'callers-edge',
      from: 'callers',
      to: 'edge',
      curve: straight({ x: 85, y: 104 }, { x: 85, y: 200 }),
      rate: 0,
    },
    // What the worker sends out (WhatsApp, status checks, refunds). The API's own
    // calls to providers (sign-in codes, asking for a UPI payment) have no line of
    // their own: the box lies beyond the worker's, and the panel shows them.
    {
      id: 'worker-providers',
      from: 'worker',
      to: 'providers',
      curve: link({ x: 820, y: 76 }, { x: 850, y: 76 }),
      rate: ov.providers.callsPerMinute.worker,
    },
  ];

  const edges: MapEdge[] = spec.map((s) => {
    const from = byId.get(s.from)!;
    const to = byId.get(s.to)!;
    const measured = s.inbound === true || (!unmeasured.has(s.from) && !unmeasured.has(s.to));
    let state: EdgeState = 'ok';
    let rate = s.rate;
    let drop = false;
    if (!measured || rate < 0.2) {
      state = 'idle';
      rate = measured ? rate : 0;
    } else if (from.status === 'bad' || to.status === 'bad') {
      state = 'bad';
    } else if (from.status === 'warn' || to.status === 'warn') {
      state = 'warn';
    }
    if (measured && to.status === 'bad') drop = true;
    return {
      id: s.id,
      from: s.from,
      to: s.to,
      curve: s.curve,
      path: cubicPath(s.curve),
      rate,
      dps: state === 'idle' ? 0 : dotsPerSecond(rate),
      state,
      drop,
    };
  });
  return { nodes, edges };
}
